import * as THREE from 'three';
import type { UnitAnimator } from './unit-animator.js';
import { graphicsOptions } from '../graphics-options.js';
import { isSkinnedShadowDepth } from './figure-shadows.js';
import { SKIN_VOLUME, VolumeSkinnedMesh, withFrozenBones } from './volume-skin.js';
import { rigidFigureMaterial, shareFigureMaterials } from './figure-materials.js';
import { paintPalette, paletteMaterial } from './character-palettes.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

let bakeTime: number | undefined, bakeDeadline = 0, mergeTime: number | undefined;
/** Bone matrix drift (metres, or unitless rotation) below which a frozen corpse keeps its uploaded pose. */
const STILL = 1e-5;
/** Ground movement (metres) of a body at rest that wakes it again. */
const MOVED = 1e-4;

/** Surfaces a settled corpse bakes into rigid geometry (`optimiseSettledCorpse` leaves morphed and cloth-caged ones skinned). */
const bakes = (object: THREE.Object3D): object is THREE.SkinnedMesh =>
  object instanceof THREE.SkinnedMesh && !object.geometry.userData.nimblezAnimated && !Object.keys(object.geometry.morphAttributes).length;

/**
 * A dead unit is drawn fully opaque (the scene clears transparency from its standard surfaces), so the stand-in compiles that state: a
 * copy with the surface's own shader patch and program key, which three then shares with the surface. Fog of war already patched the
 * original; the copy opts out, so it is not patched twice.
 */
function deadSurface(material: THREE.Material) {
  if (!(material instanceof THREE.MeshStandardMaterial) || !material.transparent) return material;
  const copy = material.clone();
  copy.onBeforeCompile = material.onBeforeCompile; copy.customProgramCacheKey = material.customProgramCacheKey;
  copy.transparent = false; copy.userData = { ...copy.userData, noFogOfWar: true };
  return copy;
}

/** A surface a corpse at rest folds into its merged draw: solid and untextured, its colour and finish kept per vertex. */
function mergeable(mesh: THREE.Mesh): mesh is THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial> {
  const material = mesh.material, geometry = mesh.geometry;
  if (mesh instanceof THREE.InstancedMesh || !(material instanceof THREE.MeshStandardMaterial) || material instanceof THREE.MeshPhysicalMaterial || geometry.userData.nimblezAnimated) return false;
  if (material.transparent || material.opacity !== 1 || material.alphaTest || material.alphaHash || material.userData.shared || Object.values(material).some(value => value instanceof THREE.Texture)) return false;
  if (Object.keys(geometry.morphAttributes).length || geometry.drawRange.start !== 0 || geometry.drawRange.count !== Infinity) return false;
  return ['position', 'normal'].every(name => { const attribute = geometry.getAttribute(name); return attribute && !(attribute as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute; });
}
const shown = (object: THREE.Object3D, root: THREE.Object3D) => { for (let up: THREE.Object3D | null = object; up; up = up.parent) { if (!up.visible) return false; if (up === root) return true; } return false; };
/** The material of a corpse's merged draw: a two-sided palette surface (capes and hair strands are drawn from both sides). */
function corpseMaterial() { return paletteMaterial(new THREE.MeshStandardMaterial({ side: THREE.DoubleSide })); }

/**
 * A rigid stand-in for every surface of these figures a corpse would bake, to compile while the scene prepares: the rigid shader of a
 * figure material otherwise first linked when its first body came to rest, mid-fight. The stand-ins share the surfaces' geometry and
 * materials; remove them after compiling without disposing anything.
 */
export function corpseShaderWarmup(figures: Iterable<THREE.Object3D>) {
  const roots = [...figures];
  const group = new THREE.Group(), seen = new Set<THREE.Material>();
  group.name = 'corpse-shader-warmup';
  for (const root of roots) root.traverse(object => {
    if (!bakes(object) || seen.has(object.material as THREE.Material)) return;
    seen.add(object.material as THREE.Material);
    const stand = new THREE.Mesh(object.geometry, deadSurface(rigidFigureMaterial(object.material as THREE.Material)));
    stand.castShadow = object.castShadow; stand.receiveShadow = object.receiveShadow;
    stand.customDepthMaterial = isSkinnedShadowDepth(object.customDepthMaterial) ? undefined : rigidFigureMaterial(object.customDepthMaterial);
    stand.customDistanceMaterial = rigidFigureMaterial(object.customDistanceMaterial);
    group.add(stand);
  });
  // The merged draw of a corpse at rest (`optimiseSettledCorpse`), skinned like the figure it stands for.
  for (const root of roots) {
    let source: THREE.SkinnedMesh | undefined;
    root.traverse(object => { if (!source && object instanceof THREE.SkinnedMesh) source = object; });
    if (!source) continue;
    const geometry = new THREE.BufferGeometry();
    for (const [name, size] of [['position', 3], ['normal', 3], ['color', 3], ['paletteFinish', 2], ['paletteEmission', 3], ['skinWeight', 4], ['skinVolume', 1]] as const)
      geometry.setAttribute(name, new THREE.Float32BufferAttribute(new Float32Array(3 * size), size));
    geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(new Uint16Array(12), 4));
    const stand = new VolumeSkinnedMesh(geometry, corpseMaterial()); stand.castShadow = stand.receiveShadow = true; stand.name = 'settled-corpse-warmup'; stand.frustumCulled = false;
    stand.bind(source.skeleton, source.bindMatrix); shareFigureMaterials(stand); group.add(stand);
    break;
  }
  return group;
}

/** Preserve the exact final skin surface, amortizing the one-time bake across frames. */
export function optimiseSettledCorpse(root: THREE.Object3D, animator: UnitAnimator) {
  const skins: THREE.SkinnedMesh[] = [];
  root.traverse(object => { if (object instanceof THREE.SkinnedMesh && !object.geometry.userData.nimblezAnimated) skins.push(object); });
  const baked = new Map<THREE.SkinnedMesh, THREE.BufferGeometry>(), depths = new Map<THREE.SkinnedMesh, THREE.Material>();
  /** The skinned materials of baked surfaces that drew with shared ones, which a rigid twin replaces while baked (figure-materials.ts). */
  const skinnedMaterials = new Map<THREE.SkinnedMesh, Pick<THREE.Mesh, 'material' | 'customDepthMaterial' | 'customDistanceMaterial'>>();
  const skeletons = [...new Set(skins.map(mesh => mesh.skeleton))];
  const updates = skeletons.map(skeleton => skeleton.update);
  const snapshots = skeletons.map(skeleton => new Float64Array(skeleton.bones.length * 16));
  let frozen = false, cursor = 0;
  let pending: { mesh: THREE.SkinnedMesh; geometry: THREE.BufferGeometry; vertex: number } | undefined;
  const point = new THREE.Vector3(), direction = new THREE.Vector4();
  // At rest, the figure's solid surfaces (skinned or riding a bone) fold into one skinned draw and hide, and nothing is baked: a field of
  // corpses otherwise drew about ten surfaces each, and their shadows again, and baking them all on the CPU fell behind in a big fight.
  // Only the figure's own surfaces fold, not arrows lodged in it since; caged cloth, see-through and textured surfaces keep their draws.
  const originals: THREE.Mesh[] = []; root.traverse(object => { if (object instanceof THREE.Mesh) originals.push(object); });
  let merged: THREE.SkinnedMesh | undefined, folded: THREE.Mesh[] = [], mergeTried = false;
  function unmerge() {
    if (merged) { merged.removeFromParent(); merged.geometry.dispose(); merged = undefined; }
    for (const mesh of folded) mesh.visible = true;
    folded = []; mergeTried = false;
  }
  function merge() {
    mergeTried = true;
    const reference = originals.find((mesh): mesh is THREE.SkinnedMesh => mesh instanceof THREE.SkinnedMesh && mesh.isSkinnedMesh);
    if (!reference) return;
    root.updateMatrixWorld(true);
    const skeleton = reference.skeleton, bindMatrix = reference.bindMatrix, bindInverse = bindMatrix.clone().invert(), bones = new Map(skeleton.bones.map((bone, i) => [bone as THREE.Object3D, i]));
    const matrix = new THREE.Matrix4(), rest = new THREE.Matrix4(), normalMatrix = new THREE.Matrix3(), pieces: THREE.BufferGeometry[] = [], members: THREE.Mesh[] = [], offsets: number[] = [];
    let vertices = 0;
    for (const mesh of originals) {
      if (!mergeable(mesh) || !shown(mesh, root)) continue;
      const source = mesh.geometry, count = source.getAttribute('position').count, geometry = new THREE.BufferGeometry();
      let skinIndex: THREE.BufferAttribute, skinWeight: THREE.BufferAttribute;
      if (mesh instanceof THREE.SkinnedMesh) {
        // Its vertices already sit in the figure's bind space.
        if (!mesh.isSkinnedMesh || mesh.skeleton !== skeleton || mesh.bindMode !== THREE.AttachedBindMode || !mesh.bindMatrix.equals(bindMatrix)) continue;
        geometry.setAttribute('position', (source.getAttribute('position') as THREE.BufferAttribute).clone());
        geometry.setAttribute('normal', (source.getAttribute('normal') as THREE.BufferAttribute).clone());
        skinIndex = new THREE.Uint16BufferAttribute(Uint16Array.from((source.getAttribute('skinIndex') as THREE.BufferAttribute).array), 4);
        skinWeight = new THREE.Float32BufferAttribute(Float32Array.from((source.getAttribute('skinWeight') as THREE.BufferAttribute).array), 4);
        const volume = source.getAttribute(SKIN_VOLUME) as THREE.BufferAttribute | undefined;
        geometry.setAttribute(SKIN_VOLUME, new THREE.Float32BufferAttribute(volume ? Float32Array.from(volume.array) : new Float32Array(count), 1));
      } else {
        // A rigid part rides the nearest bone above it: placed in bind space where that bone's skinning puts it back where it hangs now.
        let bone: THREE.Object3D | null = mesh.parent; while (bone && !bones.has(bone)) bone = bone.parent;
        if (!bone) continue;
        const b = bones.get(bone)!;
        rest.copy(skeleton.boneInverses[b]).invert();
        matrix.copy(bone.matrixWorld).invert().premultiply(rest).premultiply(bindInverse).multiply(mesh.matrixWorld);
        normalMatrix.getNormalMatrix(matrix);
        geometry.setAttribute('position', (source.getAttribute('position') as THREE.BufferAttribute).clone().applyMatrix4(matrix));
        geometry.setAttribute('normal', (source.getAttribute('normal') as THREE.BufferAttribute).clone().applyNormalMatrix(normalMatrix));
        skinIndex = new THREE.Uint16BufferAttribute(new Uint16Array(count * 4).map((_, i) => i % 4 ? 0 : b), 4);
        skinWeight = new THREE.Float32BufferAttribute(new Float32Array(count * 4).map((_, i) => i % 4 ? 0 : 1), 4);
        geometry.setAttribute(SKIN_VOLUME, new THREE.Float32BufferAttribute(new Float32Array(count), 1));
      }
      geometry.setAttribute('skinIndex', skinIndex); geometry.setAttribute('skinWeight', skinWeight);
      // A palette surface already carries its colour and finish per vertex; any other surface's are written from its material.
      const material = mesh.material;
      if (material.name === 'character-palette' && source.hasAttribute('paletteFinish')) for (const name of ['color', 'paletteFinish', 'paletteEmission'])
        geometry.setAttribute(name, (source.getAttribute(name) as THREE.BufferAttribute).clone());
      else paintPalette(geometry, material, source.getAttribute('color'));
      geometry.setIndex(source.index ? Array.from(source.index.array) : Array.from({ length: count }, (_, i) => i));
      pieces.push(geometry); members.push(mesh); offsets.push(vertices); vertices += count;
    }
    const geometry = members.length > 1 ? mergeGeometries(pieces) : null;
    for (const piece of pieces) piece.dispose();
    if (!geometry) return;
    merged = new VolumeSkinnedMesh(geometry, corpseMaterial());
    merged.name = 'settled-corpse'; merged.receiveShadow = true; merged.castShadow = members.some(member => member.castShadow);
    merged.userData.members = members.map((mesh, i) => ({ mesh, offset: offsets[i] }));
    root.add(merged); merged.bind(skeleton, bindMatrix); merged.matrixAutoUpdate = false; merged.updateMatrixWorld(true);
    // Poses reach beyond the rest bounds; cull against the whole figure, as its surfaces do.
    merged.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 1, 0), 2.4);
    shareFigureMaterials(merged);
    for (const mesh of members) { mesh.visible = false; folded.push(mesh); }
    // The merged draw casts the head's shadow itself.
    for (const mesh of originals) if (mesh.userData.headShadow && mesh.visible) { mesh.visible = false; folded.push(mesh); }
    // The pose stays put, so the bones upload once more and then only if they move.
    frozen = true;
  }
  function restore() {
    unmerge();
    pending?.geometry.dispose(); pending = undefined; cursor = 0;
    for (const [mesh, geometry] of baked) {
      mesh.geometry.dispose(); mesh.geometry = geometry;
      Object.defineProperty(mesh, 'isSkinnedMesh', { value: true, configurable: true });
      delete (mesh as unknown as Record<string, unknown>).getVertexPosition;
      delete (mesh as unknown as Record<string, unknown>).raycast;
    }
    for (const [mesh, materials] of skinnedMaterials) Object.assign(mesh, materials);
    for (const [mesh, depth] of depths) mesh.customDepthMaterial = depth;
    baked.clear(); depths.clear(); skinnedMaterials.clear(); frozen = false;
  }
  skeletons.forEach((skeleton, index) => {
    skeleton.update = function () {
      if (!frozen) { updates[index].call(this); return; }
      // Bone matrices are rebuilt every frame with rounding noise; only a real move from the pose last uploaded re-uploads it.
      const snapshot = snapshots[index]; let changed = false;
      for (let b = 0; b < this.bones.length && !changed; b++) for (let e = 0; e < 16; e++) {
        if (Math.abs(snapshot[b * 16 + e] - this.bones[b].matrixWorld.elements[e]) > STILL) { changed = true; break; }
      }
      if (!changed) return;
      for (let b = 0; b < this.bones.length; b++) snapshot.set(this.bones[b].matrixWorld.elements, b * 16);
      updates[index].call(this);
    };
  });
  function bake(time: number) {
    // All units share the interpolated game time: bound total bake work, not work per corpse.
    if (bakeTime !== time) { bakeTime = time; bakeDeadline = performance.now() + 1; }
    if (!pending && cursor === skins.length) return;
    if (performance.now() >= bakeDeadline) return;
    root.updateMatrixWorld(true);
    frozen = true;
    if (!pending && cursor < skins.length) {
      const mesh = skins[cursor++];
      pending = { mesh, geometry: mesh.geometry.clone(), vertex: 0 };
    }
    if (!pending) return;
    const { mesh, geometry } = pending, count = geometry.getAttribute('position').count;
    const end = Math.min(count, pending.vertex + 2048);
    let i = pending.vertex;
    // The pose is fixed while a slice bakes, so each bone's skinning matrix is made once for it.
    i = withFrozenBones([mesh], () => {
      let v = i;
      for (; v < end; v++) {
        if (v % 32 === 0 && performance.now() >= bakeDeadline) break;
        mesh.getVertexPosition(v, point); geometry.getAttribute('position').setXYZ(v, point.x, point.y, point.z);
        for (const name of ['normal', 'tangent']) {
          const input = mesh.geometry.getAttribute(name), output = geometry.getAttribute(name);
          if (!input) continue;
          direction.set(input.getX(v), input.getY(v), input.getZ(v), 0);
          mesh.applyBoneTransform(v, direction);
          output.setXYZ(v, direction.x, direction.y, direction.z);
        }
      }
      return v;
    });
    pending.vertex = i;
    if (i !== count) return;
    geometry.deleteAttribute('skinIndex'); geometry.deleteAttribute('skinWeight'); geometry.deleteAttribute('skinVolume');
    geometry.computeBoundingBox(); geometry.computeBoundingSphere();
    baked.set(mesh, mesh.geometry); mesh.geometry = geometry;
    Object.defineProperty(mesh, 'isSkinnedMesh', { value: false, configurable: true });
    mesh.getVertexPosition = THREE.Mesh.prototype.getVertexPosition;
    mesh.raycast = THREE.Mesh.prototype.raycast;
    // Rigid geometry draws its shadow with three's own depth material, like every other rigid surface.
    if (isSkinnedShadowDepth(mesh.customDepthMaterial)) { depths.set(mesh, mesh.customDepthMaterial!); mesh.customDepthMaterial = undefined; }
    const { material, customDepthMaterial, customDistanceMaterial } = mesh;
    if (!Array.isArray(material) && [material, customDepthMaterial, customDistanceMaterial].some(item => item?.userData.nimblezShared)) {
      skinnedMaterials.set(mesh, { material, customDepthMaterial, customDistanceMaterial });
      mesh.material = rigidFigureMaterial(material); mesh.customDepthMaterial = rigidFigureMaterial(customDepthMaterial); mesh.customDistanceMaterial = rigidFigureMaterial(customDistanceMaterial);
    }
    pending = undefined;
  }
  // Morph normals have a separate shader path, so retain their skinning until supported exactly.
  for (let i = skins.length - 1; i >= 0; i--) if (Object.keys(skins[i].geometry.morphAttributes).length) skins.splice(i, 1);
  // With corpse shadows off, a body that has come to rest stops casting until it rises or the setting returns.
  const shadowless: THREE.Object3D[] = [];
  function shade(off: boolean) {
    if (off && !shadowless.length) root.traverse(object => { if (object.castShadow) { object.castShadow = false; shadowless.push(object); } });
    else if (!off) for (const object of shadowless.splice(0)) object.castShadow = true;
  }
  let previous: { x: number; z: number; time: number } | undefined;
  const update = animator.update.bind(animator);
  animator.update = (...args) => {
    const [state, position, time] = args;
    // Interpolated positions wobble in the last bits even for a body that has not moved; only a real shift (a shove, a rewind) counts.
    if (!state.health.dead || animator.animateSettledCorpses || previous && (Math.abs(position.x - previous.x) > MOVED || Math.abs(position.z - previous.z) > MOVED || time < previous.time)) restore();
    update(...args);
    if (state.health.dead && animator.settledCorpse && !animator.animateSettledCorpses) {
      // A merge copies the figure's buffers (a few milliseconds): bodies coming to rest together take one frame each.
      if (!mergeTried && mergeTime !== time) { mergeTime = time; merge(); }
      if (mergeTried && !merged) bake(time);
    } else restore();
    shade(state.health.dead && !!animator.settledCorpse && !graphicsOptions.corpseShadows);
    // The merged draw casts as its surfaces would, so the corpse-shadow setting reaches it either way.
    if (merged) merged.castShadow = folded.some(member => member.castShadow);
    previous = { x: position.x, z: position.z, time };
  };
  const reset = animator.reset.bind(animator); animator.reset = (...args) => { restore(); reset(...args); };
  const sever = animator.sever?.bind(animator); if (sever) animator.sever = (...args) => { restore(); return sever(...args); };
  const dispose = animator.dispose.bind(animator); animator.dispose = () => {
    restore(); shade(false); skeletons.forEach((skeleton, index) => { skeleton.update = updates[index]; }); dispose();
  };
}
